<template>
  <b-modal
    v-model="show"
    title="Metas e conquistas"
    ok-title="Fechar"
    ok-variant="primary"
    size="lg"
    :scrollable="true"
    hide-footer
    @ok="show = false"
  >
    <template v-if="badgeData">
      <!-- Section title -->
      <h5 class="text-center mb-2">{{ sectionTitle }}</h5>

      <!-- Level timeline -->
      <div class="medal-timeline">
        <div
          v-for="(level, idx) in badgeData.levels"
          :key="level.threshold"
          class="medal-timeline__node"
          :class="{
            'medal-timeline__node--earned':  level.earned,
            'medal-timeline__node--current': level.threshold === currentThreshold,
          }"
        >
          <!-- Connector line (except before first) -->
          <div v-if="idx > 0" class="medal-timeline__line" :class="{ 'medal-timeline__line--earned': level.earned }" />

          <!-- Medal icon -->
          <div class="medal-timeline__icon">
            <img
              :src="level.earned ? level.imageActive : level.imageInactive"
              :alt="level.name"
              class="medal-timeline__img"
            />
          </div>

          <!-- Level name + threshold -->
          <span class="medal-timeline__name" :style="level.earned ? { color: levelColor(level.name) } : {}">
            {{ level.name }}
          </span>
          <span class="medal-timeline__threshold">{{ formatThreshold(level.threshold) }}</span>

          <!-- Achievement date badge -->
          <span v-if="level.achieved_at" class="medal-timeline__date">
            {{ formatDate(level.achieved_at) }}
          </span>
        </div>
      </div>

      <!-- Progress detail panel -->
      <div class="medal-detail mt-3">
        <div class="medal-detail__header">
          <img :src="currentImage" alt="Nível atual" class="medal-detail__img" />
          <div>
            <p class="medal-detail__level">{{ badgeData.name }}</p>
            <p class="medal-detail__count">
              <strong>{{ badgeData.current_count }}</strong>
              <span v-if="badgeData.next_threshold"> / {{ badgeData.next_threshold }} </span>
              <span class="medal-detail__label">{{ sectionTitle }}</span>
            </p>
            <p v-if="badgeData.next_threshold" class="medal-detail__hint">
              Faltam <strong>{{ badgeData.next_threshold - badgeData.current_count }}</strong>
              para atingir <strong>{{ badgeData.next_level }}</strong>
            </p>
            <p v-else class="medal-detail__hint medal-detail__hint--max">
              Nível máximo atingido!
            </p>
            <p v-if="badgeData.achieved_at" class="medal-detail__achieved">
              Conquistado em {{ formatDate(badgeData.achieved_at) }}
            </p>
          </div>
        </div>

        <!-- Progress bar toward next level -->
        <b-progress
          v-if="badgeData.next_threshold"
          :value="badgeData.progress_pct"
          :max="100"
          animated
          :variant="progressVariant"
          height="12px"
          class="mt-2"
          show-progress
        />
      </div>

      <!-- Role badge (badgeId === 1): show a single image -->
    </template>

    <template v-else-if="badgeId === 1">
      <div class="text-center">
        <img
          src="https://futurogfp-assets.s3.sa-east-1.amazonaws.com/medalhas/Cargos/Cargos.png"
          style="max-width: 100%; border-radius: 12px"
          alt="Nível PN"
        />
      </div>
    </template>

    <template v-else>
      <div class="text-center text-muted py-4">Carregando...</div>
    </template>
  </b-modal>
</template>

<script>
const LEVEL_COLORS = {
  bronze:   '#CD7F32',
  prata:    '#C0C0C0',
  ouro:     '#FFD700',
  platina:  '#E5E4E2',
  diamante: '#7DF9FF',
}

const BADGE_TITLES = {
  2: 'Entrevistas Agendadas',
  3: 'Assinaturas',
  4: 'Entrevistas Pagas (EP)',
  5: 'Gestão de Risco (Vida)',
  6: 'Aquisição de Bens',
  7: 'Faturamento Total',
}

export default {
  name: 'BadgesModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value:     { type: Boolean, default: false },
    badgeId:   { type: Number, default: null },
    badgeData: { type: Object, default: null },
  },
  computed: {
    show: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    sectionTitle() {
      return BADGE_TITLES[this.badgeId] || 'Medalhas'
    },
    currentThreshold() {
      return this.badgeData?.value || 0
    },
    currentImage() {
      return this.badgeData?.imageActive || this.badgeData?.imageInactive || ''
    },
    progressVariant() {
      const name = (this.badgeData?.name || '').toLowerCase()
      const map = { bronze: 'warning', prata: 'secondary', ouro: 'warning', platina: 'light', diamante: 'info' }
      return map[name] || 'primary'
    },
  },
  methods: {
    levelColor(name) {
      return LEVEL_COLORS[name.toLowerCase()] || '#718096'
    },
    formatDate(iso) {
      if (!iso) return null
      return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    formatThreshold(value) {
      if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(0)}M`
      if (value >= 1_000)    return `${(value / 1_000).toFixed(0)}k`
      return String(value)
    },
  },
}
</script>

<style scoped>
/* ── Timeline ─────────────────────────────────────────────────────────────── */
.medal-timeline {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 0;
  padding: 16px 8px;
  overflow-x: auto;
}

.medal-timeline__node {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 80px;
  flex: 1;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.medal-timeline__node--earned {
  opacity: 1;
}

.medal-timeline__node--current {
  opacity: 1;
}

.medal-timeline__line {
  position: absolute;
  top: 30px;
  right: 50%;
  width: 100%;
  height: 3px;
  background: #2d3748;
  z-index: 0;
}

.medal-timeline__line--earned {
  background: linear-gradient(90deg, #FFD700, #CD7F32);
}

.medal-timeline__icon {
  position: relative;
  z-index: 1;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #2d3748;
}

.medal-timeline__node--earned .medal-timeline__icon {
  border-color: #FFD700;
  box-shadow: 0 0 12px rgba(255, 215, 0, 0.4);
  animation: timelineGlow 2.5s ease-in-out infinite;
}

.medal-timeline__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.medal-timeline__name {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.medal-timeline__threshold {
  font-size: 10px;
  color: #718096;
}

.medal-timeline__date {
  font-size: 9px;
  color: #4a5568;
  font-style: italic;
  margin-top: 2px;
  background: #1a2035;
  padding: 1px 5px;
  border-radius: 4px;
}

/* ── Detail panel ──────────────────────────────────────────────────────────── */
.medal-detail {
  background: #1a2035;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #2d3748;
}

.medal-detail__header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.medal-detail__img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 50%;
  flex-shrink: 0;
}

.medal-detail__level {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #e2e8f0;
}

.medal-detail__count {
  margin: 4px 0 0;
  font-size: 13px;
  color: #a0aec0;
}

.medal-detail__count strong {
  color: #FFD700;
  font-size: 18px;
}

.medal-detail__label {
  color: #718096;
  font-size: 11px;
  margin-left: 4px;
}

.medal-detail__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #718096;
}

.medal-detail__hint--max {
  color: #FFD700;
  font-weight: 600;
}

.medal-detail__achieved {
  margin: 4px 0 0;
  font-size: 11px;
  color: #4a5568;
  font-style: italic;
}

/* ── Animations ──────────────────────────────────────────────────────────── */
@keyframes timelineGlow {
  0%, 100% { box-shadow: 0 0 6px rgba(255, 215, 0, 0.3); }
  50%       { box-shadow: 0 0 18px rgba(255, 215, 0, 0.7); }
}

@media (min-width: 1230px) {
  :deep(.modal-lg) { max-width: 1000px; }
}
</style>
